import type { ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "bizpandit-ui";
import { queryClient } from "@/lib/react-query/queryClient";
import { ErrorBoundary } from "@/shared/layout/ErrorBoundary";
import AuthProvider  from "./AuthProvider";
import { AccessProvider } from "@/features/access";
import { useAuthStore } from "@/stores/authStore";

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Sits inside AuthProvider so a user exists before access is fetched, and keys
 * the query on the user id — switching accounts, including exiting
 * impersonation, refetches rather than serving the previous user's access.
 */
function AccessGate({ children }: { children: ReactNode }) {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  return <AccessProvider userId={userId}>{children}</AccessProvider>;
}

/** Composition root for every cross-cutting provider in the app. */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AccessGate>{children}</AccessGate>
        </AuthProvider>
        {/*
          The one toast renderer. It must be mounted or `toast()` queues into
          an array that nothing drains — which is what the app did for a while,
          silently swallowing every message from the library's toast API.

          There were two toast libraries here until 2026-08-10; sonner is gone
          and its 43 call sites now resolve to the design system's own toast,
          which matches their `toast.success(...)` shape deliberately so the
          move was an import change rather than 218 rewrites.
        */}
        <Toaster />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
